<script>
import {
  appendSignature,
  removeSignature,
  extractTextFromMarkdown,
  isBodyEmptyOrSignatureOnly,
  SENTENCE_END_SOURCE,
} from 'dashboard/helper/editorHelper';
import { createTypingIndicator } from '@chatwoot/utils';

const TYPING_INDICATOR_IDLE_TIME = 4000;
const SENTENCE_END_BEFORE_CARET = new RegExp(`${SENTENCE_END_SOURCE}$`, 'u');
export default {
  props: {
    placeholder: {
      type: String,
      default: '',
    },
    value: {
      type: String,
      default: '',
    },
    minHeight: {
      type: Number,
      default: 2,
    },
    maxHeight: {
      type: [String, Number],
      default: '',
    },
    signature: {
      type: String,
      default: '',
    },
    rows: {
      type: Number,
      default: 2,
    },
    // add this as a prop, so that we won't have to add useUISettings
    sendWithSignature: {
      type: Boolean,
      default: false,
    },
    // allowSignature is a kill switch, ensuring no signature methods are triggered except when this flag is true
    allowSignature: {
      type: Boolean,
      default: false,
    },
    height: {
      type: Number,
      default: null,
    },
    autoCapitalizeFirstLetter: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      typingIndicator: createTypingIndicator(
        () => {
          this.$emit('typingOn');
        },
        () => {
          this.$emit('typingOff');
        },
        TYPING_INDICATOR_IDLE_TIME
      ),
    };
  },
  computed: {
    cleanedSignature() {
      // clean the signature, this will ensure that we don't have
      // any markdown formatted text in the signature
      return extractTextFromMarkdown(this.signature);
    },
  },
  watch: {
    height: {
      handler(newHeight, oldHeight) {
        if (newHeight === null && oldHeight !== null) {
          this.resizeTextarea();
        }
      },
      flush: 'post',
    },
    value() {
      // Resize AFTER Vue patches the new value into the DOM. This watcher runs
      // before the render watcher, so measuring synchronously would read the
      // PREVIOUS value's scrollHeight — a programmatic clear (e.g. after sending
      // a message) would otherwise keep the old, expanded height. Typing still
      // resizes instantly via onInput; this only fixes programmatic changes.
      this.$nextTick(() => this.resizeTextarea());
      // 🚨 watch triggers every time the value is changed, we cannot set this to focus then
      // when this runs, it sets the cursor to the end of the body, ignoring the signature
      // Suppose if someone manually set the cursor to the middle of the body
      // and starts typing, the cursor will be set to the end of the body
      // A surprise cursor jump? Definitely not user-friendly.
      if (document.activeElement !== this.$refs.textarea) {
        this.$nextTick(() => {
          this.setCursor();
        });
      }
    },
    sendWithSignature(newValue) {
      if (this.allowSignature) {
        this.toggleSignatureInEditor(newValue);
      }
    },
    maxHeight() {
      // Re-clamp when the available viewport height changes (window resize):
      // maxHeight is recomputed from window height, and without this the box
      // could stay taller than the new limit until the next input.
      this.resizeTextarea();
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.value) {
        this.resizeTextarea();
        this.setCursor();
      } else {
        this.focus();
      }
    });
  },
  methods: {
    getMaxHeightPx() {
      if (!this.maxHeight) return Infinity;
      if (typeof this.maxHeight === 'number') return this.maxHeight;
      if (typeof this.maxHeight === 'string' && this.maxHeight.endsWith('%')) {
        return (parseFloat(this.maxHeight) / 100) * window.innerHeight;
      }
      return parseFloat(this.maxHeight) || Infinity;
    },
    resizeTextarea() {
      const textarea = this.$refs.textarea;
      if (!textarea) return;

      textarea.style.height = 'auto';
      const minHeightPx = this.minHeight * 16;
      const contentHeight = textarea.scrollHeight;

      if (this.height !== null) {
        if (contentHeight > this.height) {
          // Content overflows controlled height — expand immediately and release control
          textarea.style.height = `${Math.min(
            contentHeight,
            this.getMaxHeightPx()
          )}px`;
          this.$emit('release-height');
        } else {
          // Content fits — restore the controlled height (we set 'auto' above for measuring)
          textarea.style.height = `${this.height}px`;
        }
        return;
      }

      textarea.style.height =
        contentHeight > minHeightPx
          ? `${Math.min(contentHeight, this.getMaxHeightPx())}px`
          : `${this.minHeight}rem`;
    },
    // The toggleSignatureInEditor gets the new value from the
    // watcher, this means that if the value is true, the signature
    // is supposed to be added, else we remove it.
    toggleSignatureInEditor(signatureEnabled) {
      const valueWithSignature = signatureEnabled
        ? appendSignature(this.value, this.cleanedSignature)
        : removeSignature(this.value, this.cleanedSignature);

      this.$emit('input', valueWithSignature);

      this.$nextTick(() => {
        this.resizeTextarea();
        this.setCursor();
      });
    },
    setCursor() {
      const bodyWithoutSignature = removeSignature(
        this.value,
        this.cleanedSignature
      );

      // only trim at end, so if there are spaces at the start, those are not removed
      const bodyEndsAt = bodyWithoutSignature.trimEnd().length;
      const textarea = this.$refs.textarea;

      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(bodyEndsAt, bodyEndsAt);
      }
    },
    isStartingNewDraft(newValue, caret) {
      // The draft is "new" when everything except the just-typed character is
      // signature boilerplate (or nothing at all): a signature-only draft still
      // counts as new, while a draft the agent has actually written into does
      // not, even with the caret back at position 0. Shares one definition with
      // the ProseMirror composer via the helper.
      return isBodyEmptyOrSignatureOnly(
        newValue.slice(caret),
        this.cleanedSignature
      );
    },
    isStartingNewLine(newValue, caret) {
      return caret === 1 || newValue[caret - 2] === '\n';
    },
    isStartingNewSentence(newValue, caret) {
      return SENTENCE_END_BEFORE_CARET.test(newValue.slice(0, caret - 1));
    },
    // Terminal punctuation is unambiguous, so it fires anywhere — including
    // mid-line and inside text the agent already wrote. The start of a line is
    // a weaker signal: it is equally true when the caret was moved back to the
    // start of finished text, which is an edit, so there the rule also requires
    // that nothing follows on the line. The signature escape hatch applies only
    // at position 1: that is the one place a non-blank remainder still means
    // "brand new draft", because ReplyBox pre-filled it with the signature.
    // Further down, a signature-only remainder just means the caret sits inside
    // the signature, which must be left alone.
    startsASentence(newValue, caret) {
      return (
        this.isStartingNewSentence(newValue, caret) ||
        (this.isStartingNewLine(newValue, caret) &&
          (this.isRestOfLineBlank(newValue, caret) ||
            (caret === 1 && this.isStartingNewDraft(newValue, caret))))
      );
    },
    isRestOfLineBlank(newValue, caret) {
      const nextBreak = newValue.indexOf('\n', caret);
      const restOfLine =
        nextBreak === -1
          ? newValue.slice(caret)
          : newValue.slice(caret, nextBreak);
      return restOfLine.trim() === '';
    },
    onInput(event) {
      let newValue = event.target.value;
      const textarea = event.target;
      // Fires when a single lowercase letter is typed where a sentence starts.
      // Composed characters (dead-key accents) never reach this path — they
      // arrive as `insertCompositionText` and are handled on compositionend.
      const caret = textarea.selectionStart;
      const isFirstTypedLetter =
        this.autoCapitalizeFirstLetter &&
        event.inputType === 'insertText' &&
        event.data?.length === 1 &&
        /^\p{Ll}$/u.test(event.data) &&
        this.startsASentence(newValue, caret);
      if (isFirstTypedLetter) {
        newValue =
          newValue.slice(0, caret - 1) +
          event.data.toUpperCase() +
          newValue.slice(caret);
        textarea.value = newValue;
        // Assigning .value resets the caret to the end, which is only
        // harmless when the whole value is the single typed character.
        // Now that trailing content (e.g. a signature) can follow, restore
        // the caret to just after the capitalized letter.
        textarea.setSelectionRange(caret, caret);
      }
      this.$emit('input', newValue);
      this.resizeTextarea();
    },
    // A dead-key accent (' then a => á) never fires an `insertText` input
    // event, so onInput cannot see it. The composed character is already in the
    // textarea by the time this runs.
    onCompositionEnd(event) {
      const textarea = event.target;
      const composed = event.data;
      const caret = textarea.selectionStart;
      const { value } = textarea;
      const isComposedLowercase =
        this.autoCapitalizeFirstLetter &&
        composed?.length === 1 &&
        /^\p{Ll}$/u.test(composed) &&
        value[caret - 1] === composed;
      if (!isComposedLowercase || !this.startsASentence(value, caret)) {
        return;
      }
      const newValue =
        value.slice(0, caret - 1) + composed.toUpperCase() + value.slice(caret);
      textarea.value = newValue;
      textarea.setSelectionRange(caret, caret);
      this.$emit('input', newValue);
      this.resizeTextarea();
    },
    onKeyup() {
      this.typingIndicator.start();
    },
    onBlur() {
      this.typingIndicator.stop();
      this.$emit('blur');
    },
    onFocus() {
      this.$emit('focus');
    },
    focus() {
      if (this.$refs.textarea) this.$refs.textarea.focus();
    },
    blur() {
      if (this.$refs.textarea) this.$refs.textarea.blur();
    },
  },
};
</script>

<template>
  <div class="resizable-textarea-container">
    <textarea
      ref="textarea"
      class="resizable-textarea text-sm"
      :placeholder="placeholder"
      :rows="rows"
      :value="value"
      :style="height !== null ? { height: height + 'px' } : null"
      @input="onInput"
      @compositionend="onCompositionEnd"
      @focus="onFocus"
      @keyup="onKeyup"
      @blur="onBlur"
    />
  </div>
</template>

<style scoped lang="scss">
.resizable-textarea-container {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.resizable-textarea {
  resize: none;
  width: 100%;
  border: 0;
  outline: none;
  background: transparent;
  padding: 0;

  &:focus {
    outline: none;
    border: 0;
    background: transparent;
  }
}
</style>
